import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import { treatExperiment } from '#src/app/shared/api/experiments';

import type { PetitionDetailsDataExperimentsQuery } from '../experiments.graphql';

type Experiment = PetitionDetailsDataExperimentsQuery['experiments']['signFormGdprRadioRemovalExperiment'];

type Params = {
	experiment: Experiment;
	utilityContext: UtilityContext;
	/** The `gdpr_sign_opt_in` FCM, surfaced via `experiments.gdprSignOptInCopy.config.enabled`. */
	gdprSignOptInEnabled: boolean;
	gdprConsentProvided: boolean;
	signed: boolean;
};

/**
 * Returns the full experiment object rather than just `variation`, since `<TrackExperimentExposure>`
 * needs `treated`/`fallback` to decide whether to fire `$exposure`.
 *
 * `shouldRunExperiment` must match what actually makes GdprConsentField render, or we bucket
 * visitors whose UI is identical in both arms and dilute measured lift. `gdprSignOptInEnabled &&
 * !gdprConsentProvided` is exactly `useGdprConfig`'s `enabled`, read from the same two sources — keep
 * it that way. Gates that mirror a render condition are also safe against the page-user-data refetch
 * (`loginState`/`currentUserId` are `hookDeps`, so this re-resolves mid-session): when such a gate
 * flips, the radios are already hidden, so forcing `control` changes nothing on screen.
 *
 * Note the two FCMs are *different*: `isEnabledByFC` below covers this experiment's own
 * `sign_form_gdpr_radio_removal_experiment` FCM, which says nothing about whether the radios render —
 * hence the separate `gdprSignOptInEnabled`.
 *
 * `isGdprCountry(countryCode)` is left out and can't be added here: it reads the live `countryCode`
 * *form* field, which doesn't exist at page-user-data resolution time and is mutable afterwards
 * (useAddressField rewrites it from a Google address pick). FCM country targeting is the practical
 * stand-in, via `fallback`.
 *
 * `signed` is the one gate that does NOT mirror a render condition: signed visitors still get a live
 * sign form when `unauthenticatedSignFormEnabled` is on (Side.tsx only swaps in SideSigned when it is
 * off). So a guest who buckets into v1, then turns out on refetch to have already signed, is forced
 * back to `control` and sees the radios reappear. Deliberate population scoping, kept knowingly.
 */
export async function normalizeSignFormGdprRadioRemovalExperiment({
	experiment,
	utilityContext,
	gdprSignOptInEnabled,
	gdprConsentProvided,
	signed,
}: Params): Promise<Experiment> {
	if (!shouldRunExperiment({ experiment, gdprSignOptInEnabled, gdprConsentProvided, signed })) {
		// Bucketing is keyed on UUID alone, so these can arrive holding a treatment arm from an
		// earlier visit that *was* in scope — overwrite both.
		return { ...experiment, variation: 'control', treated: false };
	}

	if (experiment.override) {
		// `useTrackExperimentExposure` no-ops `$exposure` for `override`. Don't force `treated: false`
		// here — that guard runs first and would suppress the override's QA log.
		return experiment;
	}

	await treatExperiment(utilityContext, experiment.experimentName);

	return { ...experiment, treated: true };
}

function shouldRunExperiment({
	experiment,
	gdprSignOptInEnabled,
	gdprConsentProvided,
	signed,
}: Omit<Params, 'utilityContext'>): boolean {
	return isEnabledByFC(experiment) && gdprSignOptInEnabled && !gdprConsentProvided && !signed;
}

/**
 * `enabledIf` failing (this experiment's own FCM `enabled` being false for the country) surfaces as
 * `fallback: true`, not as `treated: false` — so `fallback` is the signal. It also covers resolution
 * failures (`fetch-error`, `not-found`, …), equally unsafe to treat on.
 */
function isEnabledByFC(experiment: Experiment): boolean {
	return !experiment.fallback;
}
